import dayjs from 'dayjs';
import {makeAutoObservable, reaction, when} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {ConferenceApiModel} from '../../api/conference';
import type {CoreClientConferencesCreateParams} from '../../api/types/core/methods';
import type {CalendarController} from './Calendar.controller';
import type {TimeslotsStore} from './Timeslots.store';
import type {ConferenceType, DataModel, Strategy} from './types';

import {ISOStringToDatetimeObj} from '../../utils/datetime';
import {SCHEDULE_SLOT_DURATION} from '../../utils/entity/user';
import {
  FlagManager,
  InitialLoadManager,
  ReactionDisposeManager,
} from '../../utils/store';
import {CoachModel} from './Coach.model';
import {CoachesStore} from './Coaches.store';
import {type TimeslotModel} from './Timeslot.model';

export class ExternalIntroCallStrategy implements Strategy {
  static AVAILABLE_PERIOD_FOR_BOOKING_IN_DAYS = 14;
  static MIN_BOOKING_LEAD_MINUTES = 60;
  coachCoverage = null;
  coaches = new CoachesStore();
  conferenceToRescheduleId: null | string = null;
  conferenceType: ConferenceType = 'live';
  dataTarget: DataModel | null = null;
  disposer = new ReactionDisposeManager();
  hasCoachCoverage = false;
  initialLoad = new InitialLoadManager();
  introCallConference: ConferenceApiModel | null = null;
  isIntroCallAvailable = new FlagManager();
  readonly isShowBuyMoreButton = false;
  readonly isShowCoachSelect = false;
  readonly shouldShowAvailableConferences = false;
  private calendar: CalendarController;
  private externalAuthData: TimeslotsStore.ExternalAuthData;
  private timeslots: TimeslotsStore;

  constructor() {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  async init(): Promise<void> {
    this.calendar.init({
      end: dayjs()
        .add(
          ExternalIntroCallStrategy.AVAILABLE_PERIOD_FOR_BOOKING_IN_DAYS,
          'day',
        )
        .endOf('day')
        .valueOf(),
      start: dayjs().startOf('day').valueOf(),
    });

    this.dataTarget = new CoachModel({
      avatar: null,
      coach_intro_call_length: SCHEDULE_SLOT_DURATION,
      coach_schedule_until: ISOStringToDatetimeObj(
        dayjs().add(1, 'month').endOf('day').toISOString(),
      ),
      coach_title: 'Dummy Coach',
      name: 'Dummy Coach',
    });

    this.timeslots.setMinBookingLeadMinutes(
      ExternalIntroCallStrategy.MIN_BOOKING_LEAD_MINUTES,
    );

    this.initialLoad.setDone();
  }

  destruct() {
    this.disposer.dispose();
  }

  get conferenceDuration() {
    return this.dataTarget?.conferenceDuration ?? SCHEDULE_SLOT_DURATION;
  }

  get isTimeslotsLoaded() {
    return Boolean(this.timeslots?.initialLoad.isDone);
  }

  get shouldGuessTimezone() {
    return true;
  }

  get shouldShowBookButton() {
    return this.dataTarget?.shouldShowBookButton ?? false;
  }

  get shouldShowTimezone() {
    return true;
  }

  get title() {
    return i18n.t('shared:schedule_session.live');
  }

  async deleteConferenceToReschedule(): Promise<void> {
    // do nothing
  }

  inject(calendar: CalendarController, timeslots: TimeslotsStore) {
    this.calendar = calendar;
    this.timeslots = timeslots;

    return this;
  }

  isCoverageCoachAvailableForSlot(): boolean {
    return true;
  }

  makeRequestParams(
    _forSlot: TimeslotModel,
  ): CoreClientConferencesCreateParams {
    throw new Error(
      "It's not allowed to make request params for PreregisteredIntroCallStrategy",
    );
  }

  async refresh(): Promise<void> {
    return Promise.resolve();
  }

  refreshSlots({isSilent}: {isSilent?: boolean} = {}) {
    return this.timeslots.makeSlots({
      coaches: [],
      duration: this.conferenceDuration,
      externalAuthData: this.externalAuthData,
      isSilent,
    });
  }

  setConferenceToRescheduleId(_id: null | string) {
    throw new Error(
      "It's not allowed to change conference to reschedule for IntroCallStrategy",
    );
  }

  setConferenceType(_type: ConferenceType) {
    throw new Error(
      "It's not allowed to change conference type for IntroCallStrategy",
    );
  }

  setExternalAuthData(externalAuthData: TimeslotsStore.ExternalAuthData) {
    this.externalAuthData = externalAuthData;
  }

  setupReactions(shouldFetchTimeslots: boolean) {
    const fetchTimeslots = () => {
      if (shouldFetchTimeslots) {
        this.refreshSlots();
      }
    };

    this.disposer.watch(
      'initial_load',
      when(
        () => this.initialLoad.isDone,
        () => {
          fetchTimeslots();

          this.disposer.watch(
            'change_range',
            reaction(
              () => this.calendar.range,
              () => {
                fetchTimeslots();
              },
            ),
          );
        },
      ),
    );

    return this;
  }
}
